import { useEffect, useRef, useState } from 'react';

const useScrollToActiveElement = ({ elementRefs, threshold = 0.6, scrollDuration = 1000 }) => {
  const [activeName, setActiveName] = useState(null);
  const [isProgrammaticScroll, setIsProgrammaticScroll] = useState(false);
  const observerRef = useRef(null); // Ref for the observer

  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      entries => {
        if (isProgrammaticScroll) return; // NOTE: Skip updates during programmatic scroll

        const visibleElements = entries
          .filter(entry => entry.isIntersecting)
          .map(entry => ({
            name: entry.target.getAttribute('data-name'),
            rect: entry.boundingClientRect,
          }));

        if (visibleElements.length > 0) {
          visibleElements.sort((a, b) => {
            const aDistance = Math.abs(a.rect.top + a.rect.height / 2 - window.innerHeight / 2);
            const bDistance = Math.abs(b.rect.top + b.rect.height / 2 - window.innerHeight / 2);
            return aDistance - bDistance;
          });

          setActiveName(visibleElements[0].name); // NOTE Set the most prominent element
        }
      },
      {
        root: null,
        threshold,
      },
    );

    Object.values(elementRefs.current).forEach(ref => {
      if (ref) observerRef.current.observe(ref);
    });

    return () => {
      if (observerRef.current) observerRef.current.disconnect();
    };
  }, [elementRefs, isProgrammaticScroll, threshold]);

  const scrollToElement = name => {
    if (elementRefs.current[name]) {
      setIsProgrammaticScroll(true); // NOTE: Disable observer updates during programmatic scroll

      elementRefs.current[name].scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });

      setActiveName(name);

      setTimeout(() => setIsProgrammaticScroll(false), scrollDuration);
    }
  };

  return { activeName, scrollToElement };
};

export default useScrollToActiveElement;
